/**
 * @file Small math helpers shared across systems.
 * Kept dependency-free on purpose: none of these justify a library.
 */

export const TAU = Math.PI * 2;

/**
 * @param {number} value Value to constrain.
 * @param {number} min Lower bound.
 * @param {number} max Upper bound.
 * @returns {number}
 */
export function clamp(value, min, max) {
  return value < min ? min : value > max ? max : value;
}

/**
 * @param {number} a Start value.
 * @param {number} b End value.
 * @param {number} t Interpolation factor.
 * @returns {number}
 */
export function lerp(a, b, t) {
  return a + (b - a) * t;
}

/**
 * Frame-rate independent exponential smoothing.
 * `lambda` is the rate of approach: bigger means snappier.
 *
 * @param {number} current Current value.
 * @param {number} target Target value.
 * @param {number} lambda Approach rate (units per second).
 * @param {number} dt Delta time in seconds.
 * @returns {number}
 */
export function damp(current, target, lambda, dt) {
  return lerp(current, target, 1 - Math.exp(-lambda * dt));
}

/**
 * @param {number} min Inclusive lower bound.
 * @param {number} max Exclusive upper bound.
 * @returns {number}
 */
export function randomRange(min, max) {
  return min + Math.random() * (max - min);
}

/**
 * Shortest signed difference between two angles, in (-PI, PI].
 * @param {number} from Source angle in radians.
 * @param {number} to Target angle in radians.
 * @returns {number}
 */
export function angleDelta(from, to) {
  let delta = (to - from) % TAU;
  if (delta > Math.PI) delta -= TAU;
  if (delta < -Math.PI) delta += TAU;
  return delta;
}

/**
 * Smoothly rotates an angle towards another one.
 * @param {number} current Current angle in radians.
 * @param {number} target Target angle in radians.
 * @param {number} lambda Approach rate.
 * @param {number} dt Delta time in seconds.
 * @returns {number}
 */
export function dampAngle(current, target, lambda, dt) {
  return current + angleDelta(current, target) * (1 - Math.exp(-lambda * dt));
}

/**
 * Squared planar distance on the XZ plane (no square root, no allocation).
 * @param {number} ax First point X.
 * @param {number} az First point Z.
 * @param {number} bx Second point X.
 * @param {number} bz Second point Z.
 * @returns {number}
 */
export function distanceSqXZ(ax, az, bx, bz) {
  const dx = bx - ax;
  const dz = bz - az;
  return dx * dx + dz * dz;
}

/**
 * @param {number} degrees Angle in degrees.
 * @returns {number} Angle in radians.
 */
export function toRadians(degrees) {
  return (degrees * Math.PI) / 180;
}
